import * as React from 'react';
import styles from './index.less';
import FModal from '@/components/FModal';
import FInput from '@/components/FInput';
// import FSelect2 from '@/components/FSelect2';
import FComponentsLib from '@freelog/components-lib';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import fMessage from '@/components/fMessage';
import * as AHooks from 'ahooks';
import fPromiseModalConfirm from '@/components/fPromiseModalConfirm';

interface FWithdrawModalProps {
  accountID: string;
  open?: boolean;
  availableAmount: number;
  // accountInfo: string;
  onCancel?: () => void;
  onOk?: (amount: string) => void;
}

interface FWithdrawModalStates {
  amount: string;
  amountError: string;
  bankCardInfo: string;
  committing: boolean;
}

const initStates: FWithdrawModalStates = {
  amount: '',
  amountError: '',
  bankCardInfo: '',
  committing: false,
};

function FWithdrawModal({
  accountID,
  open = true,
  availableAmount,
  // accountInfo,
  onCancel,
  onOk,
}: FWithdrawModalProps) {
  const [$amount, set$amount, get$amount] = FUtil.Hook.useGetState<FWithdrawModalStates['amount']>(
    initStates['amount'],
  );
  const [$amountError, set$amountError, get$amountError] = FUtil.Hook.useGetState<
    FWithdrawModalStates['amountError']
  >(initStates['amountError']);

  const [$bankCardInfo, set$bankCardInfo, get$bankCardInfo] = FUtil.Hook.useGetState<
    FWithdrawModalStates['bankCardInfo']
  >(initStates['bankCardInfo']);

  const [$committing, set$committing, get$committing] = FUtil.Hook.useGetState<FWithdrawModalStates['committing']>(
    initStates['committing'],
  );

  AHooks.useDebounceEffect(
    () => {
      if (accountID !== '') {
        handleBankCardInfo();
      }
    },
    [accountID],
    {
      wait: 100,
    },
  );

  AHooks.useDebounceEffect(
    () => {
      if (open) {
        handleBankCardInfo();
      }
    },
    [open],
    {
      wait: 100,
    },
  );

  const handleWithdrawAll = () => {
    set$amount(availableAmount.toString());
    set$amountError(availableAmount > 10000 ? '提现金额不能大于1万元' : '');
  };

  async function handleBankCardInfo() {
    if (get$bankCardInfo() !== '' || accountID === '') {
      return;
    }

    const {
      ret: ret2,
      errCode: errCode2,
      msg: msg2,
      data: data2,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        accountId: string;
        bankName: string;
        cardNo: string;
        cardType: number;
        ownerUserId: number;
        provId: string;
        areaId: string;
      };
    } = await FServiceAPI.Payment.queryBindWithdrawCard({
      accountId: accountID,
    });
    if (ret2 !== 0 || errCode2 !== 0) {
      fMessage(msg2, 'error');
      return;
    }

    if (!data2) {
      return;
    }

    set$bankCardInfo(`${data2.bankName}（${data2.cardNo.slice(-4)}）`);
  }

  const handleConfirm = async () => {

    set$committing(true);

    const {
      ret,
      errCode,
      msg,
    }: {
      ret: number;
      errCode: number;
      msg: string;
    } = await FServiceAPI.Payment.withdrawCash({
      accountId: accountID,
      transactionAmount: Number(get$amount()),
    });
    set$committing(false);
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }
    // fMessage(msg, 'success');
    onOk?.(get$amount());
  };

  return (
    <FModal title={null} width={660} open={open} closable={true} onCancel={onCancel} footer={null}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <div className={styles.title}>{
            // 提现
            FI18n.i18nNext.tAuto('withdraw_title')
          }</div>
        </div>

        <div className={styles.content}>
          <div className={styles.inputSection}>
            <div className={styles.labelWrapper}>
              <div className={styles.label}>{
                // 提现金额
                FI18n.i18nNext.tAuto('withdraw_input_amount')
              }</div>
            </div>
            <div className={styles.inputWrapper}>
              <FInput
                wrapClassName={styles.input}
                className={styles.input}
                // placeholder='请输入金额...'
                value={$amount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    set$amount('');
                    // set$amountError('请输入提现金额');
                    set$amountError('');
                    return;
                  }
                  // 验证：判断 value 是否是大于等于0的数字，最多2位小数（允许输入过程中的小数点）
                  if (!/^(0|[1-9]\d*)(\.\d{0,2})?$/.test(value)) {
                    // fMessage('请输入有效的金额，最多2位小数', 'error');
                    return;
                  }
                  let errorText = '';
                  if (Number(value) > availableAmount) {
                    errorText =
                      // 提现金额不能大于可提现金额
                      FI18n.i18nNext.tAuto('withdraw_alert_amountexceedbalance');
                  }
                  // else if (Number(value) === 0) {
                  //   errorText = '提现金额不能等于0';
                  // }
                  else if (Number(value) > 10000) {
                    errorText = '提现金额不能大于1万元';
                  }
                  set$amount(value);
                  set$amountError(errorText);
                }}
              />
              {$amountError !== '' && (
                <>
                  <div style={{ height: 5 }} />
                  <div style={{ color: '#EE4040' }}>{$amountError}</div>
                </>
              )}
            </div>
            <div className={styles.hintWrapper}>
              <span className={styles.hintText}>
                {
                  // 可提现金额 {availableAmount} 元
                  FI18n.i18nNext.tAuto('withdraw_msg_availabletowithdraw', { amount: availableAmount })
                }
              </span>
              <FComponentsLib.FTextBtn
                type='primary'
                onClick={handleWithdrawAll}
                style={{ fontSize: 12, lineHeight: '18px' }}
              >
                {
                  // 全部提现
                  FI18n.i18nNext.tAuto('withdraw_btn_withdrawall', {
                    amount: availableAmount.toFixed(2),
                  })
                }
              </FComponentsLib.FTextBtn>
            </div>
          </div>

          <div className={styles.selectSection}>
            <div className={styles.labelWrapper}>
              <div className={styles.label}>{
                // 结算账号
                FI18n.i18nNext.tAuto('withdraw_input_withdrawmethod')
              }</div>
            </div>
            <div className={styles.selectWrapper}>
              <FComponentsLib.FContentText type='normal' text={$bankCardInfo} />
              {/* <FSelect2
                className={styles.select}
                value={selectedAccount}
                onChange={(value) => setSelectedAccount(value as string)}
                options={accountOptions.map((option) => ({
                  label: option.label,
                  value: option.value,
                }))}
              /> */}
            </div>
          </div>

          {/* <div className={styles.tipsSection}>
            <div className={styles.tipItem}>
              <div className={styles.tipDot}></div>
              <div className={styles.tipText}>{
                // 每24小时仅可提现一次
                FI18n.i18nNext.tAuto('withdraw_helpinfo')
              }</div>
            </div>
            <div className={styles.tipItem}>
              <div className={styles.tipDot}></div>
              <div className={styles.tipText}>单次提现金额不能超过1万元</div>
            </div>
          </div> */}

          {/* <div className={styles.tipsSectionWrapper}> */}
          {FI18n.i18nNext.tAuto('withdraw_helpinfo')}
          {/* </div> */}
        </div>

        <div className={styles.footer}>
          <FComponentsLib.FRectBtn type='default' onClick={onCancel}>
            {
              // 取消
              FI18n.i18nNext.tAuto('btn_cancel')
            }
          </FComponentsLib.FRectBtn>
          <FComponentsLib.FRectBtn
            type='primary'
            onClick={handleConfirm}
            disabled={$amount === '' || $amountError !== '' || $committing || Number($amount) <= 0}
          >
            {
              // 提现
              FI18n.i18nNext.tAuto('withdraw_btn_submit')
            }
          </FComponentsLib.FRectBtn>
        </div>
      </div>
    </FModal>
  );
}

export default FWithdrawModal;
